import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Eye, EyeOff, Building2, CheckCircle2, Upload, CreditCard, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

export const BuilderRegisterPage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    abn: '',
    qbcc: '',
    phone: '',
    address: '',
    website: '',
    // Bank details
    bank_name: 'Westpac',
    bank_bsb: '',
    bank_account_number: '',
    bank_account_name: '',
    // Payment settings
    accept_credit_card: true,
    accept_bank_transfer: true,
    default_payment_terms: 'Payment due within 7 days of invoice date',
    deposit_percentage: 50,
    quote_validity_days: 30,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const { setUser, setToken } = useAuth();
  const navigate = useNavigate();
  const { token: inviteToken } = useParams();
  const [inviteState, setInviteState] = useState('checking'); // checking | valid | invalid

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await api.get(`/builder-invite/${inviteToken}`);
        setFormData((prev) => ({
          ...prev,
          email: res.data.email || '',
          company_name: res.data.company_name || '',
        }));
        setInviteState('valid');
      } catch (e) {
        setInviteState('invalid');
      }
    };
    if (inviteToken) validate();
    else setInviteState('invalid');
  }, [inviteToken]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'number' ? parseInt(value) || 0 : value 
    });
  };

  const handleSwitchChange = (name, checked) => {
    setFormData({ ...formData, [name]: checked });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo file must be less than 2MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      // Complete registration via the invite token
      const response = await api.post(`/builder-invite/${inviteToken}`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        company_name: formData.company_name,
        abn: formData.abn || null,
        qbcc: formData.qbcc || null,
        phone: formData.phone || null,
        address: formData.address || null,
        website: formData.website || null,
      });

      const { token, user, company, trial_end } = response.data;
      
      // If logo was uploaded, upload it
      if (logoFile && company?.id) {
        try {
          const logoFormData = new FormData();
          logoFormData.append('file', logoFile);
          await api.post(`/companies/${company.id}/logo`, logoFormData, {
            headers: { 
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            }
          });
        } catch (logoError) {
          console.error('Failed to upload logo:', logoError);
          // Don't fail registration if logo upload fails
        }
      }

      // Set auth state
      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);

      toast.success('Welcome! Your 30-day free trial has started.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1) {
      if (!formData.name || !formData.email || !formData.password) {
        toast.error('Please fill in all required fields');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
    }
    if (step === 2) {
      if (!formData.company_name) {
        toast.error('Company name is required');
        return;
      }
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  const totalSteps = 4;

  if (inviteState === 'checking') {
    return (
      <div className="min-h-screen bg-background bg-grid flex items-center justify-center p-4" data-testid="invite-checking">
        <p className="text-muted-foreground">Validating your invite…</p>
      </div>
    );
  }

  if (inviteState === 'invalid') {
    return (
      <div className="min-h-screen bg-background bg-grid flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-card border-border rounded-lg" data-testid="invite-invalid">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Invite not valid</CardTitle>
            <CardDescription>
              This registration link is invalid, has expired, or has already been used.
              Please request a new invite from your administrator.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Button onClick={() => navigate('/login')} className="rounded-lg">Go to Sign In</Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background bg-grid flex items-center justify-center p-4">
      <Card className="w-full max-w-xl bg-card border-border rounded-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_ezclaimwin/artifacts/aasxwc8a_ChatGPT_Image_Jan_4__2026__09_48_44_AM-removebg-preview.png" 
              alt="My Project Agent" 
              className="h-24 object-contain"
            />
          </div>
          <CardTitle className="text-2xl">Start Your Free Trial</CardTitle>
          <CardDescription>
            30 days free, then $30 AUD/month. No credit card required.
          </CardDescription>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center mt-6 space-x-1">
            {[1, 2, 3, 4].map((s, i) => (
              <React.Fragment key={s}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${step >= s ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                  {step > s ? <CheckCircle2 className="h-5 w-5" /> : s}
                </div>
                {i < 3 && <div className={`w-10 h-1 ${step > s ? 'bg-primary' : 'bg-muted'}`} />}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-2 px-2">
            <span>Account</span>
            <span>Company</span>
            <span>Bank</span>
            <span>Branding</span>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit}>
            {/* Step 1: Account Details */}
            {step === 1 && (
              <div className="space-y-4 animate-fade-in">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-input border-border rounded-lg"
                    data-testid="builder-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    readOnly
                    className="bg-input border-border rounded-lg opacity-80 cursor-not-allowed"
                    data-testid="builder-email"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        minLength={6}
                        className="bg-input border-border rounded-lg pr-10"
                        data-testid="builder-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      className="bg-input border-border rounded-lg"
                      data-testid="builder-confirm-password"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Company Details */}
            {step === 2 && (
              <div className="space-y-4 animate-fade-in">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    placeholder="ABC Builders Pty Ltd"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    className="bg-input border-border rounded-lg"
                    data-testid="builder-company-name"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="abn">ABN</Label>
                    <Input
                      id="abn"
                      name="abn"
                      placeholder="XX XXX XXX XXX"
                      value={formData.abn}
                      onChange={handleChange}
                      className="bg-input border-border rounded-lg"
                      data-testid="builder-abn"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="qbcc">QBCC License</Label>
                    <Input
                      id="qbcc"
                      name="qbcc"
                      placeholder="12345678"
                      value={formData.qbcc}
                      onChange={handleChange}
                      className="bg-input border-border rounded-lg"
                      data-testid="builder-qbcc"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    placeholder="0400 000 000"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-input border-border rounded-lg"
                    data-testid="builder-phone"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Business Address</Label>
                  <Input
                    id="address"
                    name="address"
                    placeholder="123 Builder St, Brisbane QLD"
                    value={formData.address}
                    onChange={handleChange}
                    className="bg-input border-border rounded-lg"
                    data-testid="builder-address"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    name="website"
                    placeholder="www.yourcompany.com"
                    value={formData.website}
                    onChange={handleChange}
                    className="bg-input border-border rounded-lg"
                    data-testid="builder-website"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Bank Details & Payment Settings */}
            {step === 3 && (
              <div className="space-y-4 animate-fade-in">
                <div className="text-center mb-4">
                  <Banknote className="h-10 w-10 mx-auto text-primary mb-2" />
                  <h3 className="text-lg font-semibold">Payment Details</h3>
                  <p className="text-sm text-muted-foreground">
                    These details will appear on your quotes and invoices
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    name="bank_name"
                    placeholder="Westpac"
                    value={formData.bank_name}
                    onChange={handleChange}
                    className="bg-input border-border rounded-lg"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bank_bsb">BSB</Label>
                    <Input
                      id="bank_bsb"
                      name="bank_bsb"
                      placeholder="XXX XXX"
                      value={formData.bank_bsb}
                      onChange={handleChange}
                      className="bg-input border-border rounded-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_account_number">Account Number</Label>
                    <Input
                      id="bank_account_number"
                      name="bank_account_number"
                      placeholder="XXXXXXXX"
                      value={formData.bank_account_number}
                      onChange={handleChange}
                      className="bg-input border-border rounded-lg"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_account_name">Account Name</Label>
                  <Input
                    id="bank_account_name"
                    name="bank_account_name"
                    placeholder="Your Company Name"
                    value={formData.bank_account_name || formData.company_name}
                    onChange={handleChange}
                    className="bg-input border-border rounded-lg"
                  />
                </div>

                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-sm mb-3">Payment Methods Accepted</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Banknote className="h-4 w-4" />
                        <span className="text-sm">Bank Transfer</span>
                      </div>
                      <Switch
                        checked={formData.accept_bank_transfer}
                        onCheckedChange={(checked) => handleSwitchChange('accept_bank_transfer', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        <span className="text-sm">Credit Card (Visa, Mastercard, Amex)</span>
                      </div>
                      <Switch
                        checked={formData.accept_credit_card}
                        onCheckedChange={(checked) => handleSwitchChange('accept_credit_card', checked)}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-sm mb-3">Quote & Invoice Settings</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="deposit_percentage">Deposit Required (%)</Label>
                      <Input
                        id="deposit_percentage"
                        name="deposit_percentage"
                        type="number"
                        min="0"
                        max="100"
                        value={formData.deposit_percentage}
                        onChange={handleChange}
                        className="bg-input border-border rounded-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quote_validity_days">Quote Valid (days)</Label>
                      <Input
                        id="quote_validity_days"
                        name="quote_validity_days"
                        type="number"
                        min="1"
                        max="365"
                        value={formData.quote_validity_days}
                        onChange={handleChange}
                        className="bg-input border-border rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Logo Upload */}
            {step === 4 && (
              <div className="space-y-6 animate-fade-in">
                <div className="text-center">
                  <Building2 className="h-12 w-12 mx-auto text-primary mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Upload Your Company Logo</h3>
                  <p className="text-sm text-muted-foreground">
                    This will appear on your quotes, invoices, and other documents.
                  </p>
                </div>

                <div className="flex flex-col items-center gap-4">
                  {logoPreview ? (
                    <div className="relative">
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="h-32 w-auto object-contain border rounded-lg p-2"
                      />
                      <button
                        type="button"
                        onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                      <Upload className="h-10 w-10 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">Click to upload logo</span>
                      <span className="text-xs text-muted-foreground mt-1">PNG, JPG, WebP (max 2MB)</span>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        onChange={handleLogoChange}
                        className="hidden"
                        data-testid="builder-logo-upload"
                      />
                    </label>
                  )}
                </div>

                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-sm">What's Included in Your Trial:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Full access to all features
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Unlimited jobs, quotes, and invoices
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Custom branding on documents
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      30 days free, no credit card required
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </form>
        </CardContent>

        <CardFooter className="flex justify-between">
          {step > 1 ? (
            <Button type="button" variant="outline" onClick={prevStep} className="rounded-lg">
              Back
            </Button>
          ) : (
            <div />
          )}
          
          {step < totalSteps ? (
            <Button type="button" onClick={nextStep} className="rounded-lg font-semibold">
              Continue
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={loading || !formData.company_name}
              className="rounded-lg font-semibold"
              data-testid="builder-submit"
            >
              {loading ? 'Creating Account...' : 'Start Free Trial'}
            </Button>
          )}
        </CardFooter>

        <div className="pb-6 text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:underline">
            Sign In
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default BuilderRegisterPage;
